import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { alpha } from '@mui/material/styles';

export interface PaginationGoToPageFieldProps {
  currentPage: number;
  totalPages: number;
  onCommit: (page: number) => void;
}

/**
 * Campo mínimo (solo el número) para ir a una página. Enter aplica; blur sin Enter restaura.
 */
const PaginationGoToPageField: React.FC<PaginationGoToPageFieldProps> = ({
  currentPage,
  totalPages,
  onCommit,
}) => {
  const [value, setValue] = useState(String(currentPage));

  useEffect(() => {
    setValue(String(currentPage));
  }, [currentPage]);

  if (totalPages < 1) {
    return null;
  }

  const commit = () => {
    const raw = value.trim();
    if (raw === '') {
      setValue(String(currentPage));
      return;
    }
    const n = parseInt(raw.replace(/[^\d]/g, ''), 10);
    if (Number.isNaN(n)) {
      setValue(String(currentPage));
      return;
    }
    const p = Math.min(Math.max(1, n), totalPages);
    onCommit(p);
    setValue(String(p));
  };

  return (
    <TextField
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
      onBlur={() => {
        setValue(String(currentPage));
      }}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      size="small"
      variant="outlined"
      inputProps={{
        'aria-label': 'Página',
        inputMode: 'numeric' as const,
        maxLength: 6,
      }}
      sx={(theme) => ({
        minWidth: 64,
        width: 64,
        flexShrink: 0,
        '& .MuiOutlinedInput-root': {
          backgroundColor: alpha(
            theme.palette.text.primary,
            theme.palette.mode === 'dark' ? 0.08 : 0.04,
          ),
        },
        '& .MuiOutlinedInput-input': {
          textAlign: 'center',
          py: '6.5px',
          px: 0.5,
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: 'text.primary',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '7px',
          borderStyle: 'solid',
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.dark,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
        },
      })}
    />
  );
};

export default PaginationGoToPageField;
